import { Box, EmptyState, Text } from '@dixa/lib-shared-habla';
import { usePaginatedCollections } from 'lib-knowledge-management-common/hooks/usePaginatedCollections';
import { useModal } from 'lib-knowledge-modal';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { Navigate } from 'react-router-dom';
import { useSearchParam } from 'react-use';

import { useArticlesFilters } from '../articles/hooks/useArticlesFilters';
import { CollectionAddModal } from '../collectionEdit/components/modals/collectionAddModal';
import { FullPageWrapper } from '../home/collectionRootPage';

import { useTestEmptyCollections } from './useTestEmptyCollections';

export function CollectionEmptyPage(): JSX.Element {
  const { store: articlesFiltersStore } = useArticlesFilters();
  const { data: collectionData, loading: _collectionLoading } =
    usePaginatedCollections({
      fetchPolicy: 'cache-and-network',
    });
  const collectionsLoading = !collectionData && _collectionLoading;
  const testEmptyCollections = useTestEmptyCollections();

  if (
    collectionData &&
    collectionData.collections.results.length !== 0 &&
    !testEmptyCollections
  ) {
    /**
     * Redirect because user should not be on this page if there are already
     * collections in existence.
     */
    return (
      <Navigate
        to={routeGenerator.collectionEdit({
          collectionId: collectionData?.collections.results[0].id,
          articlesFiltersStore,
        })}
        replace={true}
      />
    );
  }

  if (collectionsLoading) return <KnowledgeLoading />;

  if (!collectionData?.settings) throw new Error('No settings data found');

  return (
    <CollectionEmptyPageContent
      canCreateCollections={collectionData.settings.canCreateCollections}
    />
  );
}

type CollectionEmptyPageContentProps = {
  canCreateCollections: boolean;
};
function CollectionEmptyPageContent({
  canCreateCollections,
}: CollectionEmptyPageContentProps) {
  const { showModal } = useModal();
  const showNewCollectionModal = () => showModal(CollectionAddModal, {});
  /**
   * Testing the empty collections flow is difficult when you have pre-existing
   * collections.
   *
   * Append the preventCreateCollections=true querystring while testing the
   * `/knowledge/collection` route locally against staging and the app will
   * temporarily return 0 collections, tricking it into the "No collections"
   * flow. In addition, preventCreateCollections will mock the scenario where a
   * user does not have permission to create collections.
   *
   * To test the functionality out, simply visit
   * http://localhost:8082/knowledge/collection?testEmptyCollections=true&preventCreateCollections=true
   *
   * You should see the 'Not allowed to create a collection' notice.
   */
  const preventCreate = useSearchParam('preventCreateCollections');
  const preventCreateCollections = preventCreate
    ? JSON.parse(preventCreate)
    : false;

  if (!canCreateCollections || preventCreateCollections) {
    // Not allowed to create a collection
    return (
      <FullPageWrapper>
        <Box width="352px" padding="400">
          <EmptyState
            description={
              <Box>
                <Text.Paragraph subdued variant="2">
                  It appears that your organization does not have any knowledge
                  collections at this time. Unfortunately, you do not have the
                  necessary permissions to create collections. Please speak with
                  your manager for more information on how to gain the ability
                  to create collections.
                </Text.Paragraph>
              </Box>
            }
            title="No collections"
            icon="exclamation"
          />
        </Box>
      </FullPageWrapper>
    );
  }

  return (
    <FullPageWrapper>
      <Box width="352px" padding="400">
        <EmptyState
          description={
            <>
              Create your first collection to start organizing your content.
              {/* <Text.ExternalLink as="a" href="TODO when article written" target="_blank">
                {' '}
                Learn more.
              </Text.ExternalLink> */}
            </>
          }
          title="No collections"
          icon="file"
          buttonProps={{
            children: 'New collection',
            onClick: showNewCollectionModal,
          }}
        />
      </Box>
    </FullPageWrapper>
  );
}
